import { useState, useEffect, useMemo } from 'react';
import { useUserPermissions } from '../../../../hooks/useUserPermissions';
import { axiosGet } from '../../../../../utils/axiosGet';
import { CM_PRIMARY_ID, SE_COMMITTEE_ID } from '../../../../../utils/constants';
import useCommitteeHelpers from './useCommitteeHelpers';
import { devError } from '../../../../../utils/devLog';

const useRankingReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    isAdmin,
    isSCSecretary,
    isCouncilMember,
    userCommittees,
    permissionsLoading,
  } = useUserPermissions();

  const {
    formatCandidate,
    getCommitteeInfo,
    getLastColumnLabel,
    getCommitteeMembers,
    isChairOfCommittee,
  } = useCommitteeHelpers();

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axiosGet('ranking-report');
      setReportData(response.data.data || []);
      setError(null);
    } catch (err) {
      devError('Error fetching ranking report:', err);
      setError('Failed to load ranking report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const visibleReportData = useMemo(() => {
    if (isAdmin || isSCSecretary || isCouncilMember) return reportData;

    if (userCommittees && userCommittees.length > 0) {
      const assignedIds = userCommittees.map((c) => Number(c.id));

      const effectiveAllowedIds = assignedIds.includes(CM_PRIMARY_ID)
        ? [...assignedIds, SE_COMMITTEE_ID]
        : assignedIds;

      return reportData.filter((committee) => {
        const committeeId = Number(
          committee.sectionalCommitteeId || committee.id,
        );
        return effectiveAllowedIds.includes(committeeId);
      });
    }
    return [];
  }, [reportData, isAdmin, isSCSecretary, isCouncilMember, userCommittees]);

  const committeeMembersMap = useMemo(() => {
    const map = {};
    visibleReportData.forEach((committee) => {
      const rawId = Number(committee.sectionalCommitteeId || committee.id);
      map[rawId] = getCommitteeMembers(committee);
    });
    return map;
  }, [visibleReportData, getCommitteeMembers]);

  const csvData = useMemo(() => {
    const data = [];
    visibleReportData.forEach((committee) => {
      const { chairName, observers } = getCommitteeInfo(committee);
      const lastColLabel = getLastColumnLabel(committee);

      data.push({
        'Sectional Committee': committee.sectionalCommitteeName,
        Chair: chairName,
        Observers: observers,
        Rank: '',
        Name: '',
        Gender: '',
        'Country of Birth': '',
        Institution: '',
        [lastColLabel]: '',
        'Field of Expertise': '',
      });

      committee.candidates.forEach((candidate) => {
        const f = formatCandidate(candidate, committee.electionTypeId);
        data.push({
          'Sectional Committee': '',
          Chair: '',
          Observers: '',
          Rank: f.rank,
          Name: f.name,
          Gender: f.gender,
          'Country of Birth': f.country,
          Institution: f.institution,
          [lastColLabel]: f.stateOrCountry,
          'Field of Expertise': f.expertise,
        });
      });
      data.push({});
    });
    return data;
  }, [
    visibleReportData,
    getCommitteeInfo,
    formatCandidate,
    getLastColumnLabel,
  ]);

  return {
    loading: loading || permissionsLoading,
    error,
    visibleReportData,
    committeeMembersMap,
    csvData,
    fetchReportData,
    isChairOfCommittee,
  };
};

export default useRankingReport;
